import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid2,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useStyles } from "./styles";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import HelpIcon from "@mui/icons-material/Help";
import CachedIcon from "@mui/icons-material/Cached";
import AutoCompleteDropdown from "components/autoCompleteDropdown";

const FormModal = ({
  open,
  onClose,
  title,
  formFields,
  primaryBtnText,
  secondaryBtnAction,
  secondaryBtnText,
  rowData,
  setRefreshClicked,
  refreshLoading,
}) => {
  const theme = useTheme();
  const classes = useStyles({ theme });
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  useEffect(() => {
    setFormData(rowData);
    setErrors({});
  }, [rowData]);

  const handleChange = (e, title) => {
    const { value } = e.target;
    setFormData((prev) => ({ ...prev, [title]: value }));
    if (title === "documentation_label") {
      setErrors((prev) => ({ ...prev, documentation_link: "" }));
    }
    setErrors((prev) => ({ ...prev, [title]: "" }));
  };

  const onSubmit = () => {
    let newErrors = {};

    formFields.forEach((field) => {
      if (field.required && !formData[field.title]?.trim()) {
        newErrors[field.title] = `${field.name} is required`;
      } else if (
        field?.title === "documentation_link" &&
        formData[field.title]
      ) {
        try {
          if (!formData["documentation_label"]) {
            newErrors["documentation_label"] = "Label is required";
          }
          new URL(formData[field.title]);
        } catch (e) {
          newErrors[field.title] = "Please enter a valid URL";
        }
      } else if (
        field?.title === "documentation_label" &&
        formData[field.title] &&
        !formData["documentation_link"]
      ) {
        newErrors["documentation_link"] = "Link is required";
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      secondaryBtnAction(formData);
    }
  };

  const onDropdownChange = (evt, field, newValue) => {
    setErrors((prev) => ({ ...prev, [field?.title]: "" }));
    setFormData((prev) => ({
      ...prev,
      [field?.title]: newValue,
      is_ship_template: true,
    }));
  };

  const handleClose = () => {
    setFormData({});
    setErrors({});
    !refreshLoading && onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      classes={{ paper: classes.container }}
      data-testid={"dialog"}
      PaperProps={{ style: { overflow: "visible" } }}
    >
      <Typography
        data-testid={"title"}
        variant="subtitle1"
        className={classes.dialogTitle}
      >
        {title}
      </Typography>
      <DialogContent>
        <Grid2 container spacing={2} xs={12}>
          {formFields?.map((field) => (
            <Grid2
              sx={{
                display: "flex",
                gap: "5px",
                flexDirection: "column",
                marginTop: "10px",
              }}
              key={field.name}
              size={field.type !== "grouped" ? 12 : 6}
            >
              <InputLabel
                className={classes.input}
                shrink={false}
                htmlFor={field.name}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                  }}
                >
                  {` ${field.required ? "*" : ""} ${field.name} `}
                  {field.helpIcon && (
                    <Tooltip
                      title={`The list was last refreshed at ${field.lastRefreshedAt}. Please click on refresh icon to get latest data.`}
                      placement="right"
                    >
                      <HelpIcon className={classes.helpIcon} />
                    </Tooltip>
                  )}
                </Typography>
              </InputLabel>
              {!field.isDropdown ? (
                <TextField
                  slotProps={{
                    inputLabel: { shrink: false },
                    htmlInput: {
                      maxLength: field?.limit || undefined,
                    },
                  }}
                  id={field.name}
                  name={field.name}
                  required={field.required}
                  placeholder={field.prompt}
                  onChange={(evt) => handleChange(evt, field.title)}
                  value={formData[field.title] || ""}
                  error={!!errors[field.title]}
                  helperText={errors[field.title] || " "}
                  fullWidth
                  type={field.type || "text"}
                  variant="outlined"
                  className={classes.inputText}
                />
              ) : (
                <Grid2
                  display={"flex"}
                  flexDirection={"row"}
                  sx={{ alignItems: "center" }}
                >
                  <AutoCompleteDropdown
                    noOptionsText={"No templates found"}
                    Id="select-outlined"
                    value={formData[field.title] || rowData?.[field.title]}
                    lists={field.options}
                    setDropdownValue={(event, newValue) => {
                      onDropdownChange(event, field, newValue);
                    }}
                    errorTitle={errors[field.title] || undefined}
                  />
                  {field.helpIcon && (
                    <Tooltip title="Refresh list">
                      {refreshLoading ? (
                        <CircularProgress
                          className={classes.loadingIcon}
                          size={25}
                          color="secondary"
                          data-testid="loading"
                        />
                      ) : (
                        <CachedIcon
                          data-testid={"CachedIcon"}
                          className={classes.refreshIcon}
                          onClick={setRefreshClicked}
                        />
                      )}
                    </Tooltip>
                  )}
                </Grid2>
              )}
            </Grid2>
          ))}
        </Grid2>
      </DialogContent>
      <DialogActions
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
        }}
      >
        <Button
          data-testid={"primary-btn"}
          className={classes.btn}
          onClick={handleClose}
          disabled={refreshLoading}
        >
          {primaryBtnText}
        </Button>
        <Button
          data-testid={"secondary-btn"}
          className={classes.btn}
          type="submit"
          onClick={onSubmit}
          disabled={refreshLoading}
        >
          {secondaryBtnText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FormModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  formFields: PropTypes.array,
  primaryBtnText: PropTypes.string,
  secondaryBtnAction: PropTypes.func,
  secondaryBtnText: PropTypes.string,
  rowData: PropTypes.any,
  setRefreshClicked: PropTypes.func,
  refreshLoading: PropTypes.bool,
};

export default FormModal;
